import { useCallback, useEffect, useReducer, useMemo } from "react";

import { APIRequestData, usePrivateAPIData } from "contexts/api-data-provider";
import { useUser } from "contexts/UserContext";
import { splitToChunks } from "utils/fn";

import OrchidDriveEntryKeysReducer from "../reducers/orchidDriveEntryKeys";
import { orchid_decode_key } from "utils/OrchidEncryption";
import { DriveEntryErrorById, OrchidDriveEntryKeys } from "../types";

const ORCHID_DRIVE_ENTRY_KEY_REQUEST_CHUNK_SIZE = 256;

function getEntriesNotYetRequested(
  entries: string[],
  requests: Set<string>[],
): string[] {
  const notYetRequested: Set<string> = new Set();
  for (const entry of entries) {
    let alreadyRequested = false;
    for (const request of requests) {
      if (request.has(entry)) {
        alreadyRequested = true;
        break;
      }
    }
    if (!alreadyRequested) notYetRequested.add(entry);
  }
  return Array.from(notYetRequested);
}

type OrchidDriveEntryKeysReducerResetAction = {
  type: "reset";
};

type OrchidDriveEntryKeyRequestsReducerAddKeysAction = {
  type: "request_entries";
  entries: string[];
};

type OrchidDriveEntryKeyRequestsState = {
  requests: Array<{
    entryIds: Set<string>;
    requestData: APIRequestData;
  }>;
};

function OrchidDriveEntryKeyRequestsReducer(
  state: OrchidDriveEntryKeyRequestsState,
  action:
    | OrchidDriveEntryKeysReducerResetAction
    | OrchidDriveEntryKeyRequestsReducerAddKeysAction,
): OrchidDriveEntryKeyRequestsState {
  switch (action.type) {
    case "reset": {
      if (state.requests.length === 0) return state;
      return { requests: [] };
    }
    case "request_entries": {
      const notYetRequested = getEntriesNotYetRequested(
        action.entries,
        state.requests.map((request) => request.entryIds),
      );
      if (notYetRequested.length === 0) return state;
      const newRequests = splitToChunks(
        notYetRequested,
        ORCHID_DRIVE_ENTRY_KEY_REQUEST_CHUNK_SIZE,
      );
      return {
        requests: state.requests.concat(
          newRequests.map((chunk) => {
            return {
              entryIds: new Set(chunk),
              requestData: {
                resource: "/api/drive/keys/",
                fetchArgs: {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ entry_ids: chunk }),
                },
              },
            };
          }),
        ),
      };
    }
  }
  throw new Error(`unexpected action type: ${action}`);
}

export type EntriesNeedKeysFunc = (entryIds: string[]) => void;
export type AddEntryKeysFunc = (entryKeys: {
  [name: string]: OrchidDriveEntryKeys;
}) => void;
export type OrchidDriveKeysByEntryId = {
  [name: string]: OrchidDriveEntryKeys | null;
};

export default function OrchidDriveEntryKeysProvider(): {
  entriesNeedKeys: EntriesNeedKeysFunc;
  addEntryKeys: AddEntryKeysFunc;
  orchidDriveEntryKeys: OrchidDriveKeysByEntryId;
  orchidDriveEntryKeysError: string | undefined;
  orchidDriveEntryKeysErrorByEntryId: DriveEntryErrorById;
} {
  const { user } = useUser();
  const userSub = user?.sub;

  const [orchidDriveEntryKeys, orchidDriveEntryKeysDispatch] = useReducer(
    OrchidDriveEntryKeysReducer,
    {},
  );

  useEffect(() => orchidDriveEntryKeysDispatch({ type: "reset" }), [userSub]);

  const [orchidDriveEntryKeyRequests, orchidDriveEntryKeyRequestsDispatch] =
    useReducer(OrchidDriveEntryKeyRequestsReducer, { requests: [] });

  const entriesKeysRequests = useMemo(() => {
    const requests = {};
    for (const request of orchidDriveEntryKeyRequests.requests) {
      requests[`batch(${Array.from(request.entryIds).join(",")})`] =
        request.requestData;
    }
    return requests;
  }, [orchidDriveEntryKeyRequests]);

  useEffect(() => {
    const entries = Object.entries(orchidDriveEntryKeys)
      .filter(([entryId, keys]) => !keys)
      .map(([entryId, keys]) => entryId);
    orchidDriveEntryKeyRequestsDispatch({ type: "request_entries", entries });
  }, [orchidDriveEntryKeys]);

  const orchidDriveEntriesKeysResults = usePrivateAPIData(
    entriesKeysRequests,
    0,
  );

  useEffect(() => {
    let isCancelled = false;
    (async function () {
      const entryKeys = {};
      for (const result of Object.values(orchidDriveEntriesKeysResults)) {
        if (!result.result) continue;
        for (const entryKey of result.result.key_entries) {
          entryKeys[entryKey.entry_id] = {
            entryId: entryKey.entry_id,
            dataCipherKey: await orchid_decode_key(entryKey.data_cipher_key),
            metaDataCipherKey: await orchid_decode_key(
              entryKey.meta_data_cipher_key,
            ),
          };
          if (isCancelled) return;
        }
      }
      orchidDriveEntryKeysDispatch({ type: "add_keys", entryKeys });
    })();
    return () => {
      isCancelled = true;
    };
  }, [orchidDriveEntriesKeysResults]);

  const entriesNeedKeys = useCallback((entryIds: string[]) => {
    orchidDriveEntryKeysDispatch({
      type: "need_keys",
      entryKeysNeeded: entryIds,
    });
  }, []);

  const orchidDriveEntryKeysError = useMemo(() => {
    for (const results of Object.values(orchidDriveEntriesKeysResults)) {
      if (results.errorText) return results.errorText;
    }
    return undefined;
  }, [orchidDriveEntriesKeysResults]);

  const orchidDriveEntryKeysErrorByEntryId = useMemo(() => {
    const errorByEntryId = {};
    for (const results of Object.values(orchidDriveEntriesKeysResults)) {
      if (!results.result) continue;
      for (const errorEntry of results.result.failed_entries) {
        if (!results.result) continue;
        errorByEntryId[errorEntry.entry_id] = errorEntry.error;
      }
    }
    return errorByEntryId;
  }, [orchidDriveEntriesKeysResults]);

  const addEntryKeys = useCallback(
    (entryKeys: { [name: string]: OrchidDriveEntryKeys }) => {
      orchidDriveEntryKeysDispatch({ type: "add_keys", entryKeys });
    },
    [],
  );

  return {
    entriesNeedKeys,
    addEntryKeys,
    orchidDriveEntryKeys,
    orchidDriveEntryKeysError,
    orchidDriveEntryKeysErrorByEntryId,
  };
}
